import type { ReactNode } from "react";
import { Fragment } from "react";
import {
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import NumberInput from "components/atoms/inputs/NumberInput";
import type Article from "models/Article";
import StatutButtonGroup from "components/atoms/buttons/StatutButtonGroup/StatutButtonGroup";
import Statut from "constants/Statut";
import type CommandeArticleFormModel from "models/CommandeArticleFormModel";
import type { StatutAssociation } from "constants/SatutMap";
import commandeArticleUtil from "utils/commandeArticleUtil";
import type CommandeArticleHistory from "models/CommandeArticleHistory";

type CommandeValidationTableProps = {
  articlesList: Array<CommandeArticleFormModel>;
  setArticlesList: (c: Array<CommandeArticleFormModel>) => void;
  catalogue: Array<Article>;
  statutOptions: Array<StatutAssociation>;
  historyCommandes: Array<CommandeArticleHistory>;
};

function CommandeValidationTable({
  articlesList,
  setArticlesList,
  catalogue,
  statutOptions,
  historyCommandes,
}: Readonly<CommandeValidationTableProps>): ReactNode {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  function validateAll(): void {
    setArticlesList(
      articlesList.map((value) => ({
        ...value,
        statutArticle: Statut.EN_PREPARATION,
      }))
    );
  }

  function refuseAll(): void {
    setArticlesList(
      articlesList.map((value) => ({
        ...value,
        statutArticle: Statut.REFUSE,
      }))
    );
  }

  function updateQuantite(newQuantite: number, articleIndex: number): void {
    const tempArticleList = [...articlesList];
    tempArticleList[articleIndex].quantite = newQuantite;
    setArticlesList(tempArticleList);
  }

  function updateTaille(newTaille: string, articleIndex: number): void {
    const tempArticleList = [...articlesList];
    tempArticleList[articleIndex].taille = newTaille;
    setArticlesList(tempArticleList);
  }

  function updateStatut(newStatut: Statut | undefined, articleIndex: number): void {
    const tempArticleList = [...articlesList];
    tempArticleList[articleIndex].statutArticle = newStatut;
    setArticlesList(tempArticleList);
  }

  return (
    <TableContainer sx={{ width: "100%" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography>
                <strong>Article</strong>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography>
                <strong>Qté</strong>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography>
                <strong>Taille</strong>
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ p: 0.25, width: "280px" }}>
              <Button
                sx={{ m: 0.25 }}
                size={isSmallScreen ? "small" : "medium"}
                variant="contained"
                color="success"
                onClick={validateAll}>
                Tout Valider
              </Button>
              <Button
                sx={{ m: 0.25 }}
                size={isSmallScreen ? "small" : "medium"}
                variant="contained"
                color="error"
                onClick={refuseAll}>
                Tout Refuser
              </Button>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {articlesList.map((article, i) => {
            const foundArticle = catalogue.find((a) => a.id === article.articleId);
            const limitExceeded =
              foundArticle != null &&
              commandeArticleUtil.isLimitExceeded(historyCommandes, foundArticle, article.quantite);
            return (
              <Fragment key={article.commandeArticleNumber}>
                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>
                    <Typography>{article.articleLabel}</Typography>
                  </TableCell>
                  <TableCell>
                    <NumberInput
                      size="small"
                      value={article.quantite}
                      onChange={(newQuantite) => {
                        updateQuantite(newQuantite, i);
                      }}
                      sx={{ maxWidth: "70px" }}
                      minValue={1}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={article.taille}
                      onChange={(event) => {
                        updateTaille(event.target.value, i);
                      }}
                      fullWidth
                      size="small">
                      {foundArticle?.tailles?.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <StatutButtonGroup
                      onChangeStatut={(newStatut?: Statut) => {
                        updateStatut(newStatut, i);
                      }}
                      value={article.statutArticle}
                      statutOptions={statutOptions}
                    />
                  </TableCell>
                </TableRow>
                {(limitExceeded ?? false) && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ paddingY: 0 }}>
                      <Typography color="error">Limite de commande dépassée</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CommandeValidationTable;
